import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from 'services/api';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Switch } from 'components/ui/switch';
import { Label } from 'components/ui/label';
import { 
  Calendar, ExternalLink, Check, X, RefreshCw, Link2, Unlink,
  Clock, MapPin, ArrowLeft, CalendarPlus, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

export default function CalendarIntegration() {
  const [status, setStatus] = useState({ configured: false, connected: false });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await api.get('/calendar/status');
      setStatus(response.data);
      
      if (response.data.connected) {
        fetchEvents();
      }
    } catch (error) {
      console.error('Failed to fetch calendar status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await api.get('/calendar/events?max_results=10');
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const response = await api.get('/calendar/connect');
      if (response.data.authorization_url) {
        window.location.href = response.data.authorization_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect calendar');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect Google Calendar?')) return;
    
    try {
      await api.delete('/calendar/disconnect');
      toast.success('Google Calendar disconnected');
      setStatus({ ...status, connected: false });
      setEvents([]);
    } catch (error) {
      toast.error('Failed to disconnect calendar');
    }
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    try {
      const response = await api.post('/calendar/sync-all-shifts', {});
      toast.success(response.data.message);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync shifts');
    } finally {
      setSyncing(false);
    }
  };

  const formatEventTime = (dateTime) => {
    if (!dateTime) return '';
    const date = new Date(dateTime);
    return date.toLocaleString([], { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calendar-integration">
      {/* Connection Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-teal-500" />
            Google Calendar Integration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!status.configured ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h4 className="font-medium text-amber-800">Not Configured</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    Google Calendar integration requires setup by the administrator. 
                    Contact support if you'd like to use this feature.
                  </p>
                </div>
              </div>
            </div>
          ) : status.connected ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <Check className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-green-800">Connected</h4>
                    <p className="text-sm text-green-700">
                      Your Google Calendar is connected and syncing
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleDisconnect}
                  className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                >
                  <Unlink className="w-4 h-4 mr-2" />
                  Disconnect
                </Button>
              </div>

              {/* Sync Button */}
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Sync Care Shifts</h4>
                  <p className="text-sm text-slate-500">
                    Add your scheduled shifts to Google Calendar
                  </p>
                </div>
                <Button 
                  onClick={handleSyncAll} 
                  disabled={syncing}
                  className="bg-teal-500 hover:bg-teal-600"
                  data-testid="sync-shifts-btn"
                >
                  {syncing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  {syncing ? 'Syncing...' : 'Sync All Shifts'}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Link2 className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-800">Connect Your Calendar</h4>
                    <p className="text-sm text-slate-600 mt-1">
                      Sync your care shifts to Google Calendar for better scheduling and reminders.
                    </p>
                  </div>
                </div>
              </div>
              
              <Button 
                onClick={handleConnect} 
                disabled={connecting}
                className="w-full bg-teal-500 hover:bg-teal-600"
                data-testid="connect-calendar-btn"
              >
                {connecting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Calendar className="w-4 h-4 mr-2" />
                )}
                {connecting ? 'Connecting...' : 'Connect Google Calendar'}
              </Button>
              
              <p className="text-xs text-slate-500 text-center">
                You'll be redirected to Google to authorize access
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upcoming Events */}
      {status.connected && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Upcoming Events</CardTitle>
              <Button variant="ghost" size="sm" onClick={fetchEvents}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {events.length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No upcoming events</p>
                <p className="text-sm text-slate-400">
                  Sync your shifts or create events in Google Calendar
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {events.map((event) => (
                  <div 
                    key={event.id} 
                    className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    data-testid={`event-${event.id}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-5 h-5 text-teal-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-slate-900 truncate">{event.summary}</h4>
                      <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatEventTime(event.start)}
                        </span>
                        {event.location && (
                          <span className="flex items-center gap-1 truncate">
                            <MapPin className="w-3 h-3" />
                            {event.location}
                          </span>
                        )}
                      </div>
                    </div>
                    {event.html_link && (
                      <a 
                        href={event.html_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-teal-500 hover:text-teal-600"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Benefits */}
      <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <CardContent className="py-6">
          <h3 className="font-semibold text-lg mb-4">Calendar Sync Benefits</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-teal-500/20 flex items-center justify-center flex-shrink-0">
                <Clock className="w-4 h-4 text-teal-400" />
              </div>
              <div>
                <h4 className="font-medium text-sm">Automatic Reminders</h4>
                <p className="text-xs text-slate-400 mt-1">
                  Get notified before shifts start
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-teal-500/20 flex items-center justify-center flex-shrink-0">
                <Calendar className="w-4 h-4 text-teal-400" />
              </div>
              <div>
                <h4 className="font-medium text-sm">Unified Schedule</h4>
                <p className="text-xs text-slate-400 mt-1">
                  See all appointments in one place
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-teal-500/20 flex items-center justify-center flex-shrink-0">
                <RefreshCw className="w-4 h-4 text-teal-400" />
              </div>
              <div>
                <h4 className="font-medium text-sm">Auto-Sync</h4>
                <p className="text-xs text-slate-400 mt-1">
                  Changes sync automatically
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
